import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Spinner } from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import MoneyField from '~/Components/Form/MoneyField';
import ModalConfirmacao from '~/Components/ModalConfirmacao';
import AuthStorage from '~/Utils/AuthStorage';
import * as api from '~/Services/Service';
import GridRateio from './GridRateio';

// D2: pas dfm:790-792 — somente Real e Dólar (Delphi não tem Euro/Libra/Peso)
const OPCOES_MOEDA = [
  { value: 'R', label: 'Real' },
  { value: 'D', label: 'Dólar' },
];

// C3: pas dfm:864-875 — 11 itens exatos do cbTpDocumento
const OPCOES_TP_DOCUMENTO = [
  { value: '01', label: '01-Boleto' },
  { value: '02', label: '02-Contrato' },
  { value: '03', label: '03-NF Outros (DANFE, Transporte e etc)' },
  { value: '04', label: '04-Orçamento' },
  { value: '05', label: '05-Pedido' },
  { value: '06', label: '06-Recibo' },
  { value: '07', label: '07- NFS/Produtor Rural' },
  { value: '08', label: '08-Tributos' },
  { value: '09', label: '09-RPA' },
  { value: '10', label: '10-NFS MEI' },
  { value: '99', label: '99-Outros' },
];

// D1: pas:692 usa StrToDate('01/'+mVencimento.Text) — formato de entrada MM/YYYY.
// Aceita também 0-prefixo no mês. Retorna dígitos apenas formatados.
const formatarMascaraMMYYYY = (valor) => {
  const digits = (valor || '').replace(/\D/g, '').slice(0, 6);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
};

// MM/YYYY -> YYYY-MM-01 (paridade Delphi pas:692)
const mmyyyyParaDataBackend = (mmyyyy) => {
  if (!mmyyyy) return null;
  const m = mmyyyy.match(/^(\d{2})\/(\d{4})$/);
  if (!m) return null;
  const mes = parseInt(m[1], 10);
  const ano = parseInt(m[2], 10);
  if (mes < 1 || mes > 12 || ano < 1900) return null;
  return `${ano}-${String(mes).padStart(2, '0')}-01`;
};

// Data do backend (ISO) -> MM/YYYY para edição
const dataBackendParaMMYYYY = (iso) => {
  if (!iso) return '';
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return '';
  const mes = String(dt.getMonth() + 1).padStart(2, '0');
  const ano = dt.getFullYear();
  return `${mes}/${ano}`;
};

const formatarLabelFornecedor = (f) => {
  const nome = f.apelido_conta || f.razao_social || '';
  return `${f.codigo_conta} - ${nome}`.trim();
};

const schema = Yup.object().shape({
  codigo_grupo_empresa: Yup.number().required('Grupo Empresa obrigatório').positive().integer(),
  cartao: Yup.number().required('Cartão é obrigatório').positive().integer(),
  fornecedor: Yup.number().required('Fornecedor é obrigatório').positive().integer(),
  departamento: Yup.number().required('Departamento é obrigatório').positive().integer(),
  // D4: pas:1744-1749 — Portador obrigatório
  portador: Yup.number().typeError('Portador é obrigatório').required('Portador é obrigatório').positive().integer(),
  numero: Yup.string().max(50).nullable(),
  // C3: pas:1659-1665 — TpDocumento obrigatório
  tp_documento: Yup.string()
    .required('Tipo de Documento é obrigatório')
    .oneOf(OPCOES_TP_DOCUMENTO.map((o) => o.value), 'Tipo de Documento inválido'),
  data_compra: Yup.date()
    .required('Data da compra é obrigatória')
    .max(new Date(), 'Data da compra não pode ser futura'),
  // D1: vencimento em MM/YYYY
  vencimento_mmyyyy: Yup.string()
    .required('Vencimento é obrigatório')
    .matches(/^(0[1-9]|1[0-2])\/\d{4}$/, 'Vencimento deve estar no formato MM/AAAA'),
  valor_total: Yup.number()
    .required('Valor é obrigatório')
    .positive('Valor deve ser positivo'),
  moeda: Yup.string().required('Moeda é obrigatória').oneOf(OPCOES_MOEDA.map((o) => o.value)),
  // C4: Cotação obrigatória se moeda != 'R'
  cotacao: Yup.number()
    .nullable()
    .when('moeda', (m, s) => (m && m !== 'R' ? s.required('Cotação obrigatória').positive('Cotação deve ser > 0') : s)),
  iof: Yup.number()
    .nullable()
    .when('moeda', (m, s) => (m && m !== 'R' ? s.min(0, 'IOF não pode ser negativo') : s)),
  // D3: pas dfm:828 MaxValue=999
  parcelas: Yup.number().required().integer().min(1, 'Mín. 1 parcela').max(999, 'Máx. 999 parcelas'),
  obs1: Yup.string().max(50).nullable(),
  obs2: Yup.string().max(50).nullable(),
  obs3: Yup.string().max(50).nullable(),
  obs4: Yup.string().max(50).nullable(),
  // C13: rateio validado por VALOR (paridade Delphi pas:1752-1759)
  rateios: Yup.array()
    .min(1, 'Rateio obrigatório')
    .of(
      Yup.object().shape({
        departamento: Yup.number().typeError('Departamento obrigatório').required('Departamento obrigatório').positive(),
        percentual: Yup.number().typeError('Percentual inválido').required().min(0.01).max(100),
        valor: Yup.number().typeError('Valor inválido').required().positive(),
      })
    )
    .test('soma-valor', 'Soma dos valores do rateio deve ser igual ao valor total', function (arr) {
      if (!arr || arr.length === 0) return false;
      const total = Number(this.parent && this.parent.valor_total) || 0;
      const soma = arr.reduce((s, r) => s + (parseFloat(r.valor) || 0), 0);
      return Math.abs(soma - total) < 0.01;
    }),
});

const VALORES_INICIAIS = {
  codigo_grupo_empresa: '',
  cartao: '',
  fornecedor: '',
  departamento: '',
  portador: '',
  numero: '',
  tp_documento: '',
  data_compra: '',
  vencimento_mmyyyy: '',
  valor_total: '',
  moeda: 'R',
  cotacao: '',
  iof: '',
  parcelas: 1,
  obs1: '',
  obs2: '',
  obs3: '',
  obs4: '',
  rateios: [],
};

const ModalDespesaCartaoCredito = ({
  show, modo, codigo, geDefault, opcoesGE, onClose, onSalvo,
}) => {
  // eslint-disable-next-line no-unused-vars
  const usuario = AuthStorage.getUser();
  const tem562 = AuthStorage.temCaracteristica(562); // "Alterar Despesa do Cartão"

  const [loadingDetalhe, setLoadingDetalhe] = useState(false);
  const [loadingAcao, setLoadingAcao] = useState(false);
  const [opcoesCartao, setOpcoesCartao] = useState([]);
  const [opcoesDepartamento, setOpcoesDepartamento] = useState([]);
  // D-F-07 — Portador é uma Contas (tabela Contas, não Departamento). Delphi lDespesa usa cdConta (pas:684).
  const [opcoesPortador, setOpcoesPortador] = useState([]);
  const [opcoesFornecedor, setOpcoesFornecedor] = useState([]);
  const [loadingFornecedor, setLoadingFornecedor] = useState(false);
  const [valoresIniciais, setValoresIniciais] = useState({ ...VALORES_INICIAIS, codigo_grupo_empresa: geDefault || '' });
  const [despesaOriginal, setDespesaOriginal] = useState(null);
  const [showAlterarValor, setShowAlterarValor] = useState(false);
  const [novoValor, setNovoValor] = useState('');
  const [loadingAlterarValor, setLoadingAlterarValor] = useState(false);

  // C12: confirmação AGORA/DEPOIS de anexo (pas:611-622)
  const [showConfirmAnexo, setShowConfirmAnexo] = useState(false);
  const [payloadPendente, setPayloadPendente] = useState(null);
  const [arquivoPendente, setArquivoPendente] = useState(null);

  const ehFechada = despesaOriginal && despesaOriginal.chave_de_pagamento != null;

  useEffect(() => {
    if (!show) return;
    if ((modo === 'edit' || modo === 'view') && codigo) {
      carregarDetalhe();
    } else {
      setValoresIniciais({ ...VALORES_INICIAIS, codigo_grupo_empresa: geDefault || '' });
      setDespesaOriginal(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, modo, codigo]);

  useEffect(() => {
    const ge = valoresIniciais.codigo_grupo_empresa || geDefault;
    if (!ge) return;
    carregarLookupsGE(ge);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valoresIniciais.codigo_grupo_empresa, geDefault]);

  // C2: select de Fornecedor recarrega ao mudar GE (padrão PainelLancaChave.js)
  useEffect(() => {
    const ge = valoresIniciais.codigo_grupo_empresa || geDefault;
    if (!ge) {
      setOpcoesFornecedor([]);
      return;
    }
    setLoadingFornecedor(true);
    api.listarFornecedoresFechamentoCartao(ge)
      .then((data) => {
        const lista = (data || []).map((f) => ({
          value: f.codigo_conta,
          label: formatarLabelFornecedor(f),
          raw: f,
        }));
        setOpcoesFornecedor(lista);
      })
      .catch(() => setOpcoesFornecedor([]))
      .finally(() => setLoadingFornecedor(false));
  }, [valoresIniciais.codigo_grupo_empresa, geDefault]);

  const carregarDetalhe = async () => {
    setLoadingDetalhe(true);
    try {
      const d = await api.buscarDespesaCartaoCredito(codigo);
      setDespesaOriginal(d);
      const formatDate = (v) => {
        if (!v) return '';
        const dt = new Date(v);
        if (Number.isNaN(dt.getTime())) return '';
        return dt.toISOString().substring(0, 10);
      };
      setValoresIniciais({
        codigo_grupo_empresa: d.codigo_grupo_empresa || '',
        cartao: d.cartao || '',
        fornecedor: d.fornecedor || '',
        departamento: d.departamento || '',
        portador: d.portador || '',
        numero: d.numero || '',
        tp_documento: d.tp_documento || '',
        data_compra: formatDate(d.data_compra),
        vencimento_mmyyyy: dataBackendParaMMYYYY(d.vencimento),
        valor_total: d.valor || '',
        moeda: d.moeda || 'R',
        cotacao: d.cotacao != null ? String(d.cotacao) : '',
        iof: d.iof != null ? String(d.iof) : '',
        parcelas: 1,
        obs1: d.obs1 || '',
        obs2: d.obs2 || '',
        obs3: d.obs3 || '',
        obs4: d.obs4 || '',
        rateios: (d.rateios || []).map((r) => ({
          departamento: r.departamento,
          percentual: String(r.percentual),
          valor: String(r.valor),
        })),
      });
      await carregarLookupsGE(d.codigo_grupo_empresa);
    } catch (err) {
      const msg = err && err.message ? err.message : 'Erro ao carregar despesa';
      toast.error(msg);
      onClose();
    } finally {
      setLoadingDetalhe(false);
    }
  };

  const carregarLookupsGE = async (ge) => {
    if (!ge) return;
    try {
      const [cartoes, deptos, contas] = await Promise.all([
        api.listarCartoesCredito(ge).catch(() => []),
        api.listarDepartamentosLookupGE().catch(() => []),
        api.listarContasFechamentoCartao(ge).catch(() => []),
      ]);
      setOpcoesCartao((cartoes || []).map((c) => ({
        value: c.codigo_cartao != null ? c.codigo_cartao : c.codigo,
        label: c.nome_titular != null ? c.nome_titular : (c.descricao || String(c.codigo_cartao || c.codigo)),
      })));
      setOpcoesDepartamento((deptos || []).map((d) => ({
        value: d.codigo != null ? d.codigo : d.codigo_departamento,
        label: d.nome || d.descricao || String(d.codigo),
      })));
      // D-F-07 — Portador vem de Contas (pas:684 lDespesa usa cdConta).
      setOpcoesPortador((contas || []).map((c) => {
        const desc = c.apelido_conta || c.descricao || '';
        return {
          value: c.codigo_conta,
          label: desc ? `${c.codigo_conta} - ${desc}` : String(c.codigo_conta),
        };
      }));
    } catch (e) {
      // silencioso
    }
  };

  // Monta payload final (request body)
  const montarPayload = (values, faltaDig) => {
    const base = {
      codigo_grupo_empresa: Number(values.codigo_grupo_empresa),
      cartao: Number(values.cartao),
      fornecedor: Number(values.fornecedor),
      departamento: Number(values.departamento),
      portador: values.portador ? Number(values.portador) : null,
      numero: values.numero || null,
      tp_documento: values.tp_documento,
      data_compra: values.data_compra,
      valor_total: Number(values.valor_total),
      valor: Number(values.valor_total), // usado em Alterar (backend usa request.Valor)
      moeda: values.moeda,
      cotacao: values.moeda === 'R' ? 1 : (values.cotacao ? Number(values.cotacao) : null),
      iof: values.moeda === 'R' ? 0 : (values.iof ? Number(values.iof) : null),
      parcelas: Number(values.parcelas),
      obs1: values.obs1 || null,
      obs2: values.obs2 || null,
      obs3: values.obs3 || null,
      obs4: values.obs4 || null,
      falta_dig: faltaDig,
      rateios: (values.rateios || []).map((r) => ({
        departamento: Number(r.departamento),
        percentual: Number(r.percentual),
        valor: Number(r.valor),
      })),
    };

    // D1: vencimento MM/YYYY -> YYYY-MM-01 (paridade pas:692 IncMonth é aplicado no backend por parcela)
    const venc = mmyyyyParaDataBackend(values.vencimento_mmyyyy);
    base.vencimento_primeira_parcela = venc;
    base.vencimento = venc; // alias usado em Alterar

    return base;
  };

  const gravar = async (payload, arquivo) => {
    let resposta = null;
    if (modo === 'create') {
      resposta = await api.criarDespesaCartaoCredito(payload);
      // Se o usuário escolheu anexar agora e há arquivo + código criado, sobe o anexo
      if (arquivo && resposta && Array.isArray(resposta.codigos_criados) && resposta.codigos_criados.length > 0) {
        try {
          await api.anexarPdfDespesaCartao(resposta.codigos_criados[0], arquivo);
        } catch (e) {
          toast.warn('Despesa lançada, mas falha ao enviar anexo.');
        }
      }
      toast.success('Despesa lançada com sucesso!');
    } else {
      await api.alterarDespesaCartaoCredito({ ...payload, codigo });
      if (arquivo) {
        try {
          await api.anexarPdfDespesaCartao(codigo, arquivo);
        } catch (e) {
          toast.warn('Despesa alterada, mas falha ao enviar anexo.');
        }
      }
      toast.success('Despesa alterada com sucesso!');
    }
    return resposta;
  };

  const handleSubmit = async (values) => {
    // C12: pas:611-622 — só pergunta AGORA/DEPOIS no CREATE. No edit preserva FaltaDig original.
    if (modo === 'edit') {
      setLoadingAcao(true);
      try {
        const faltaDigOriginal = despesaOriginal && despesaOriginal.falta_dig
          ? despesaOriginal.falta_dig
          : 'S';
        const payload = montarPayload(values, faltaDigOriginal);
        const resposta = await gravar(payload, null);
        onSalvo(resposta);
      } catch (err) {
        const msg = err && err.message ? err.message : 'Erro ao salvar despesa';
        toast.error(msg);
      } finally {
        setLoadingAcao(false);
      }
      return;
    }

    // Modo create — abre confirmação AGORA/DEPOIS
    setPayloadPendente(values);
    setArquivoPendente(null);
    setShowConfirmAnexo(true);
  };

  // C12: fluxo "Anexar agora" — abre seletor de PDF, envia e continua gravação
  const handleAnexarAgora = () => {
    // Dispara input file escondido
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/pdf';
    input.onchange = async (e) => {
      const arquivo = e.target.files && e.target.files[0];
      if (!arquivo) return;
      if (arquivo.type !== 'application/pdf') {
        toast.error('Somente arquivos PDF são permitidos.');
        return;
      }
      if (arquivo.size > 10 * 1024 * 1024) {
        toast.error('Arquivo excede o limite de 10 MB.');
        return;
      }
      setShowConfirmAnexo(false);
      setLoadingAcao(true);
      try {
        const payload = montarPayload(payloadPendente, 'N');
        const resposta = await gravar(payload, arquivo);
        onSalvo(resposta);
      } catch (err) {
        const msg = err && err.message ? err.message : 'Erro ao salvar despesa';
        toast.error(msg);
      } finally {
        setLoadingAcao(false);
        setPayloadPendente(null);
        setArquivoPendente(null);
      }
    };
    input.click();
  };

  // C12: fluxo "Anexar depois" — grava com falta_dig='S'
  const handleAnexarDepois = async () => {
    setShowConfirmAnexo(false);
    setLoadingAcao(true);
    try {
      const payload = montarPayload(payloadPendente, 'S');
      const resposta = await gravar(payload, null);
      onSalvo(resposta);
    } catch (err) {
      const msg = err && err.message ? err.message : 'Erro ao salvar despesa';
      toast.error(msg);
    } finally {
      setLoadingAcao(false);
      setPayloadPendente(null);
    }
  };

  const handleAlterarValor = async () => {
    if (!novoValor || Number(novoValor) <= 0) {
      toast.error('Informe um valor maior que zero');
      return;
    }
    setLoadingAlterarValor(true);
    try {
      await api.alterarValorPosFechamento({ codigo, novo_valor: Number(novoValor) });
      toast.success('Valor alterado com sucesso!');
      setShowAlterarValor(false);
      onSalvo();
    } catch (err) {
      const msg = err && err.message ? err.message : 'Erro ao alterar valor';
      toast.error(msg);
    } finally {
      setLoadingAlterarValor(false);
    }
  };

  const titulo = modo === 'create'
    ? 'Nova Despesa de Cartão de Crédito'
    : modo === 'edit'
    ? 'Alterar Despesa de Cartão de Crédito'
    : 'Visualizar Despesa de Cartão de Crédito';

  const soLeitura = modo === 'view';

  return (
    <>
    <Modal show={show} onHide={onClose} size="lg" centered backdrop="static">
      <Modal.Header closeButton style={{ backgroundColor: '#0747a6', color: '#fff' }}>
        <Modal.Title style={{ fontSize: '1rem', color: '#fff' }}>{titulo}</Modal.Title>
      </Modal.Header>

      {loadingDetalhe ? (
        <Modal.Body className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Carregando...</p>
        </Modal.Body>
      ) : (
        <Formik
          initialValues={valoresIniciais}
          enableReinitialize
          validationSchema={soLeitura ? null : schema}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, handleChange, handleBlur, handleSubmit: formikSubmit, setFieldValue }) => (
            <Form onSubmit={formikSubmit}>
              <Modal.Body>
                <Row>
                  <Col xs={12} md={6} className="mb-2">
                    <Form.Label className="small mb-1">Grupo Empresa *</Form.Label>
                    <Select
                      styles={customStyles}
                      components={{ DropdownIndicator }}
                      isDisabled={soLeitura}
                      options={opcoesGE}
                      value={opcoesGE.find((o) => o.value === Number(values.codigo_grupo_empresa)) || null}
                      onChange={(sel) => {
                        setFieldValue('codigo_grupo_empresa', sel ? sel.value : '');
                        setFieldValue('cartao', '');
                        setFieldValue('fornecedor', '');
                        carregarLookupsGE(sel ? sel.value : null);
                      }}
                      placeholder="Selecione..."
                      noOptionsMessage={() => 'Nenhuma GE'}
                    />
                    {touched.codigo_grupo_empresa && errors.codigo_grupo_empresa && (
                      <div className="text-danger" style={{ fontSize: '0.78rem' }}>{errors.codigo_grupo_empresa}</div>
                    )}
                  </Col>

                  {/* C2: Select de Fornecedor (replica padrão PainelLancaChave.js) */}
                  <Col xs={12} md={6} className="mb-2">
                    <Form.Label className="small mb-1">Fornecedor *</Form.Label>
                    <Select
                      styles={customStyles}
                      components={{ DropdownIndicator }}
                      isDisabled={soLeitura || !values.codigo_grupo_empresa}
                      isLoading={loadingFornecedor}
                      options={opcoesFornecedor}
                      value={opcoesFornecedor.find((o) => o.value === Number(values.fornecedor)) || null}
                      onChange={(sel) => setFieldValue('fornecedor', sel ? sel.value : '')}
                      placeholder={values.codigo_grupo_empresa ? 'Selecione...' : 'Selecione a GE primeiro'}
                      noOptionsMessage={() => 'Nenhum fornecedor'}
                    />
                    {touched.fornecedor && errors.fornecedor && (
                      <div className="text-danger" style={{ fontSize: '0.78rem' }}>{errors.fornecedor}</div>
                    )}
                  </Col>

                  <Col xs={12} md={6} className="mb-2">
                    <Form.Label className="small mb-1">Cartão *</Form.Label>
                    <Select
                      styles={customStyles}
                      components={{ DropdownIndicator }}
                      isDisabled={soLeitura}
                      options={opcoesCartao}
                      value={opcoesCartao.find((o) => o.value === Number(values.cartao)) || null}
                      onChange={(sel) => setFieldValue('cartao', sel ? sel.value : '')}
                      placeholder="Selecione..."
                      noOptionsMessage={() => 'Nenhum cartão'}
                    />
                    {touched.cartao && errors.cartao && (
                      <div className="text-danger" style={{ fontSize: '0.78rem' }}>{errors.cartao}</div>
                    )}
                  </Col>

                  <Col xs={12} md={6} className="mb-2">
                    <Form.Label className="small mb-1">Departamento *</Form.Label>
                    <Select
                      styles={customStyles}
                      components={{ DropdownIndicator }}
                      isDisabled={soLeitura}
                      options={opcoesDepartamento}
                      value={opcoesDepartamento.find((o) => o.value === Number(values.departamento)) || null}
                      onChange={(sel) => setFieldValue('departamento', sel ? sel.value : '')}
                      placeholder="Selecione..."
                      noOptionsMessage={() => 'Nenhum departamento'}
                    />
                    {touched.departamento && errors.departamento && (
                      <div className="text-danger" style={{ fontSize: '0.78rem' }}>{errors.departamento}</div>
                    )}
                  </Col>

                  {/* D4: Portador obrigatório (pas:1744-1749). D-F-07 — lookup Contas (não Departamento). */}
                  <Col xs={12} md={6} className="mb-2">
                    <Form.Label className="small mb-1">Portador *</Form.Label>
                    <Select
                      styles={customStyles}
                      components={{ DropdownIndicator }}
                      isDisabled={soLeitura}
                      options={opcoesPortador}
                      value={opcoesPortador.find((o) => o.value === Number(values.portador)) || null}
                      onChange={(sel) => setFieldValue('portador', sel ? sel.value : '')}
                      placeholder="Selecione..."
                      noOptionsMessage={() => 'Nenhum'}
                    />
                    {touched.portador && errors.portador && (
                      <div className="text-danger" style={{ fontSize: '0.78rem' }}>{errors.portador}</div>
                    )}
                  </Col>

                  {/* C3: TpDocumento (pas dfm:864-875) */}
                  <Col xs={12} md={6} className="mb-2">
                    <Form.Label className="small mb-1">Tipo de Documento *</Form.Label>
                    <Select
                      styles={customStyles}
                      components={{ DropdownIndicator }}
                      isDisabled={soLeitura}
                      options={OPCOES_TP_DOCUMENTO}
                      value={OPCOES_TP_DOCUMENTO.find((o) => o.value === values.tp_documento) || null}
                      onChange={(sel) => setFieldValue('tp_documento', sel ? sel.value : '')}
                      placeholder="Selecione..."
                      noOptionsMessage={() => 'Nenhum'}
                    />
                    {touched.tp_documento && errors.tp_documento && (
                      <div className="text-danger" style={{ fontSize: '0.78rem' }}>{errors.tp_documento}</div>
                    )}
                  </Col>

                  <Col xs={12} md={6} className="mb-2">
                    <Form.Label className="small mb-1">Nº / E-Doc</Form.Label>
                    <Form.Control
                      type="text"
                      name="numero"
                      maxLength={50}
                      value={values.numero}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={soLeitura}
                    />
                  </Col>

                  <Col xs={6} md={3} className="mb-2">
                    <Form.Label className="small mb-1">Data da Compra *</Form.Label>
                    <Form.Control
                      type="date"
                      name="data_compra"
                      value={values.data_compra}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={soLeitura}
                      isInvalid={touched.data_compra && !!errors.data_compra}
                    />
                    <Form.Control.Feedback type="invalid">{errors.data_compra}</Form.Control.Feedback>
                  </Col>

                  {/* D1: Vencimento MM/YYYY (pas:692 StrToDate('01/' + mVencimento.Text)) */}
                  <Col xs={6} md={3} className="mb-2">
                    <Form.Label className="small mb-1">Vencimento (MM/AAAA) *</Form.Label>
                    <Form.Control
                      type="text"
                      name="vencimento_mmyyyy"
                      placeholder="MM/AAAA"
                      value={values.vencimento_mmyyyy}
                      onChange={(e) => setFieldValue('vencimento_mmyyyy', formatarMascaraMMYYYY(e.target.value))}
                      onBlur={handleBlur}
                      disabled={soLeitura}
                      maxLength={7}
                      isInvalid={touched.vencimento_mmyyyy && !!errors.vencimento_mmyyyy}
                    />
                    <Form.Control.Feedback type="invalid">{errors.vencimento_mmyyyy}</Form.Control.Feedback>
                  </Col>

                  <Col xs={6} md={3} className="mb-2">
                    <Form.Label className="small mb-1">Valor Total *</Form.Label>
                    <MoneyField
                      field={{ name: 'valor_total', value: values.valor_total }}
                      form={{ setFieldValue }}
                      disabled={soLeitura}
                      className="form-control"
                    />
                    {touched.valor_total && errors.valor_total && (
                      <div className="text-danger" style={{ fontSize: '0.78rem' }}>{errors.valor_total}</div>
                    )}
                  </Col>

                  {/* D3: parcelas 1..999 (pas dfm:828 MaxValue=999) */}
                  <Col xs={6} md={3} className="mb-2">
                    <Form.Label className="small mb-1">Parcelas</Form.Label>
                    <Form.Control
                      type="number"
                      name="parcelas"
                      min={1}
                      max={999}
                      value={values.parcelas}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={soLeitura || modo === 'edit'}
                      isInvalid={touched.parcelas && !!errors.parcelas}
                    />
                    <Form.Control.Feedback type="invalid">{errors.parcelas}</Form.Control.Feedback>
                  </Col>

                  <Col xs={12} md={6} className="mb-2">
                    <Form.Label className="small mb-1">Moeda</Form.Label>
                    <div className="d-flex flex-wrap" style={{ gap: 8 }}>
                      {OPCOES_MOEDA.map((op) => (
                        <Form.Check
                          key={op.value}
                          type="radio"
                          id={`moeda-${op.value}`}
                          name="moeda"
                          label={op.label}
                          value={op.value}
                          checked={values.moeda === op.value}
                          onChange={(e) => {
                            handleChange(e);
                            // C4: reseta cotacao/iof ao voltar para Real
                            if (e.target.value === 'R') {
                              setFieldValue('cotacao', '');
                              setFieldValue('iof', '');
                            }
                          }}
                          disabled={soLeitura}
                          inline
                        />
                      ))}
                    </div>
                  </Col>

                  {/* C4: Cotação e IOF condicionais — só para moeda != 'R' */}
                  {values.moeda !== 'R' && (
                    <>
                      <Col xs={6} md={3} className="mb-2">
                        <Form.Label className="small mb-1">Cotação *</Form.Label>
                        <Form.Control
                          type="number"
                          name="cotacao"
                          step="0.000001"
                          min="0"
                          value={values.cotacao}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          disabled={soLeitura}
                          isInvalid={touched.cotacao && !!errors.cotacao}
                        />
                        <Form.Control.Feedback type="invalid">{errors.cotacao}</Form.Control.Feedback>
                      </Col>
                      <Col xs={6} md={3} className="mb-2">
                        <Form.Label className="small mb-1">IOF</Form.Label>
                        <Form.Control
                          type="number"
                          name="iof"
                          step="0.000001"
                          min="0"
                          value={values.iof}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          disabled={soLeitura}
                          isInvalid={touched.iof && !!errors.iof}
                        />
                        <Form.Control.Feedback type="invalid">{errors.iof}</Form.Control.Feedback>
                      </Col>
                    </>
                  )}

                  <Col xs={12} md={2} className="mb-2">
                    <Form.Label className="small mb-1">Obs 1</Form.Label>
                    <Form.Control
                      type="text"
                      name="obs1"
                      maxLength={50}
                      value={values.obs1}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={soLeitura}
                    />
                  </Col>
                  <Col xs={12} md={2} className="mb-2">
                    <Form.Label className="small mb-1">Obs 2</Form.Label>
                    <Form.Control
                      type="text"
                      name="obs2"
                      maxLength={50}
                      value={values.obs2}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={soLeitura}
                    />
                  </Col>
                  <Col xs={12} md={2} className="mb-2">
                    <Form.Label className="small mb-1">Obs 3</Form.Label>
                    <Form.Control
                      type="text"
                      name="obs3"
                      maxLength={50}
                      value={values.obs3}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={soLeitura}
                    />
                  </Col>
                  <Col xs={12} md={2} className="mb-2">
                    <Form.Label className="small mb-1">Obs 4</Form.Label>
                    <Form.Control
                      type="text"
                      name="obs4"
                      maxLength={50}
                      value={values.obs4}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={soLeitura}
                    />
                  </Col>
                </Row>

                <hr />
                <GridRateio
                  rateios={values.rateios}
                  onChange={(nova) => setFieldValue('rateios', nova)}
                  valorTotal={Number(values.valor_total) || 0}
                  disabled={soLeitura}
                  opcoesDepartamento={opcoesDepartamento}
                />
                {touched.rateios && errors.rateios && typeof errors.rateios === 'string' && (
                  <div className="text-danger mt-1" style={{ fontSize: '0.78rem' }}>{errors.rateios}</div>
                )}

                {soLeitura && ehFechada && tem562 && (
                  <div className="mt-3">
                    <hr />
                    {!showAlterarValor ? (
                      <Button variant="warning" onClick={() => setShowAlterarValor(true)}>
                        Alterar Valor (Pós-Fechamento)
                      </Button>
                    ) : (
                      <Row className="align-items-end">
                        <Col xs={6} md={4}>
                          <Form.Label className="small mb-1">Novo Valor *</Form.Label>
                          <MoneyField
                            field={{ name: 'novo_valor', value: novoValor }}
                            form={{ setFieldValue: (_, v) => setNovoValor(v) }}
                            className="form-control"
                          />
                          <small className="text-muted">Alteração máxima: ± R$ 1,00</small>
                        </Col>
                        <Col xs={6} md="auto" className="d-flex" style={{ gap: 8 }}>
                          <Button
                            variant="warning"
                            disabled={loadingAlterarValor}
                            onClick={handleAlterarValor}
                          >
                            {loadingAlterarValor ? <Spinner animation="border" size="sm" /> : 'Confirmar'}
                          </Button>
                          <Button variant="outline-secondary" onClick={() => setShowAlterarValor(false)}>
                            Cancelar
                          </Button>
                        </Col>
                      </Row>
                    )}
                  </div>
                )}
              </Modal.Body>

              <Modal.Footer>
                <Button variant="secondary" onClick={onClose} disabled={loadingAcao}>
                  {soLeitura ? 'Fechar' : 'Cancelar'}
                </Button>
                {!soLeitura && (
                  <Button type="submit" variant="primary" disabled={loadingAcao}>
                    {loadingAcao ? <Spinner animation="border" size="sm" /> : 'Salvar'}
                  </Button>
                )}
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      )}
    </Modal>

    {/* C12: confirmação AGORA/DEPOIS (pas:611-622) */}
    <Modal show={showConfirmAnexo} onHide={() => setShowConfirmAnexo(false)} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: '1rem' }}>Vincular comprovante</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        Deseja anexar o comprovante da despesa agora ou depois?
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={() => setShowConfirmAnexo(false)}>
          Cancelar
        </Button>
        <Button variant="outline-primary" onClick={handleAnexarDepois}>
          Anexar depois
        </Button>
        <Button variant="primary" onClick={handleAnexarAgora}>
          Anexar agora
        </Button>
      </Modal.Footer>
    </Modal>
    </>
  );
};

export default ModalDespesaCartaoCredito;
